<template>
  <div class="select-starting-players">
    <h2>Select Starting 6 Players (Set {{ setNumber }})</h2>
    <div class="starting-player-list">
      <div v-for="position in positions" :key="position" class="starting-player-row">
        <label :for="'position-' + position">Position {{ position }}:</label>
        <select class="player-select" :id="'position-' + position" v-model="currentLineup[position - 1].player">
          <option v-for="player in availablePlayers(position)" :key="player.number" :value="player">
            {{ player.number }}. {{ player.name }}
          </option>
        </select>
        <span v-if="currentLineup[position - 1].roleCode" class="role-code">
          {{ currentLineup[position - 1].roleCode }}
        </span>
        <span class="setter-choice">
          <input
            type="radio"
            :id="'setter-' + position"
            :name="'setter'"
            :value="position"
            v-model="setterPosition"
          />
          <label :for="'setter-' + position">Setter</label>
        </span>
      </div>
    </div>
    <div>
      <label for="libero">Libero:</label> 
      <select class="player-select" id="libero" v-model="libero" :disabled="!hasLiberoPool">
        <option :value="null">None</option>
        <option v-for="player in availablePlayersForLibero" :key="player.number" :value="player">
          {{ player.number }}. {{ player.name }}
        </option>
      </select>
    </div>
    <div class="buttons">
      <button class="btn primary" @click="assignRandomPlayers">Assign Random Players</button>
      <button class="btn primary" @click="assignInOrder">Assign Players in Order</button>
      <button class="btn primary" @click="assignPreset('serve')">Serve</button>
      <button class="btn primary" @click="assignPreset('receive')">Receive</button>
    </div>
    <div class="buttons">
      <button class="btn secondary" @click="$emit('back-to-roster')" :disabled="!canEditRoster">Back to Roster</button>
      <button class="btn primary" @click="confirmSelection" :disabled="!allPlayersSelected || !setterSelected">Confirm Selection</button>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js'; 

export default {
  name: 'SelectStartingPlayers',
  props: {
    rosterPlayers: {
      type: Array,
      required: true
    },
    liberoPool: {
      type: Array,
      required: true
    },
    setNumber: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      positions: [1, 2, 3, 4, 5, 6],
      currentLineup: store.currentLineup.map((player, index) => ({
        rotationPosition: index + 1,
        player: player || null,
        roleCode: null
      })),
      setterPosition: null,
      libero: Array.isArray(this.liberoPool) && this.liberoPool.length ? store.currentLibero : null
    };
  },
  watch: {
    liberoPool(nextPool) {
      if (!Array.isArray(nextPool) || !nextPool.length) {
        this.libero = null;
      }
    },
    setterPosition(newSetterPosition) {
      this.applyRoleCodes(newSetterPosition);
    }
  },
  computed: {
    canEditRoster() {
      return this.setNumber === 1;
    },
    hasLiberoPool() {
      return Array.isArray(this.liberoPool) && this.liberoPool.length > 0;
    },
    allPlayersSelected() {
      // console.log('store.startingPlayers', store.startingPlayers);
      // console.log(this.currentLineup.every(item => item.player.player !== null));
      return this.currentLineup.every(item => item.player.player !== null);
    },
    setterSelected() {
      return this.setterPosition !== null;
    },
    availablePlayersForLibero() {
      // Get the players already selected for positions 1 to 6
      const selected = this.currentLineup
        .filter(item => item.player)
        .map(item => item.player);
      // Filter out the selected players from the available players
      return this.liberoPool.filter(player => !selected.includes(player));
    },
  },
  methods: {
    clearRoleCodes() {
      this.currentLineup.forEach(item => {
        item.roleCode = null;
        if (item.player) {
          item.player.role = undefined;
        }
      });
    },
    applyRoleCodes(newSetterPosition) {
      if (!newSetterPosition) {
        this.clearRoleCodes();
        return;
      }

      const roleCodes = ['S', 'OH', 'MB', 'OP', 'OH', 'MB'];
      const setterPosition = newSetterPosition;

      this.currentLineup.forEach((item, index) => {
        const relativeIndex = (index - (setterPosition - 1) + 6) % 6;
        item.roleCode = roleCodes[relativeIndex];
      });
    },
    availablePlayers(position) {
      // Get the players already selected for other positions
      const selected = this.currentLineup
        .filter((item, index) => index !== position - 1 && item.player)
        .map(item => item.player);
      // Filter out the selected players from the available players
      if (this.libero) {
        selected.push(this.libero);
      }
      return this.rosterPlayers.filter(player => !selected.includes(player));
    },
    assignPreset(type) {
      // Serve: setter pos 1. Receive: rotate lineup so setter is pos 2.
      const serveOrder = [
        'zoe-morrison', 'rhea-hackin', 'katie-white',
        'louise-francois', 'maria-forson', 'chelsea-ezeagba'
      ];
      const playerIds = type === 'receive'
        ? [serveOrder[5], ...serveOrder.slice(0, 5)]  // chelsea, zoe, rhea, katie, louise, maria
        : serveOrder;
      const setterPos = type === 'receive' ? 2 : 1;

      const findPlayer = (id) => this.rosterPlayers.find(p => p.id === id);

      playerIds.forEach((id, index) => {
        const player = findPlayer(id);
        if (player) {
          this.currentLineup[index].player = player;
        }
      });

      this.setterPosition = setterPos;

      // Assign first available libero
      const liberoCandidates = this.availablePlayersForLibero;
      this.libero = liberoCandidates.length ? liberoCandidates[0] : null;
    },
    assignInOrder() {
      this.positions.forEach((position, index) => {
        this.currentLineup[position - 1].player = this.rosterPlayers[index];
      });
      if (this.setterPosition) {
        this.applyRoleCodes(this.setterPosition);
      }
      // Assign the next available player for the Libero position
      const liberoCandidates = this.availablePlayersForLibero;
      this.libero = liberoCandidates.length ? liberoCandidates[0] : null;
    },
    assignRandomPlayers() {
      const availablePlayers = [...this.rosterPlayers];
      this.positions.forEach(position => {
        const randomIndex = Math.floor(Math.random() * availablePlayers.length);
        this.currentLineup[position - 1].player = availablePlayers.splice(randomIndex, 1)[0];
      });
      if (this.setterPosition) {
        this.applyRoleCodes(this.setterPosition);
      }
      // Assign a random player for the Libero position
      const selectedPlayers = this.currentLineup.map(item => item.player);
      const liberoCandidates = this.liberoPool.filter(player => !selectedPlayers.includes(player));
      if (!liberoCandidates.length) {
        this.libero = null;
        return;
      }
      const randomIndex = Math.floor(Math.random() * liberoCandidates.length);
      this.libero = liberoCandidates[randomIndex];
    },
    confirmSelection() {
      // console.log('this.setterPosition', this.setterPosition);
      if (!this.setterPosition) {
        console.error('Setter position not selected', this.currentLineup);
        return;
      }

      const roleMap = {
        S: 'setter',
        OP: 'opposite',
        OH: 'outside',
        MB: 'middle'
      };

      this.currentLineup.forEach(item => {
        if (item.player && item.roleCode) {
          item.player.role = roleMap[item.roleCode] || undefined;
        }
      });

      const rotationId = `P${this.setterPosition}`;
      
      store.setCurrentLineup(this.currentLineup, this.libero);
      // console.log('emit update-starting-players', JSON.stringify(this.currentLineup), rotationId);
      this.$emit('update-starting-players', this.currentLineup, rotationId, this.libero);
    }
  }
};
</script>

<style scoped>
.select-starting-players {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.starting-player-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  width: fit-content;
}

.starting-player-list {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.starting-player-row .player-select {
  flex: 1;
  min-width: 0;
  max-width: 200px;
}

.role-code {
  min-width: 28px;
  font-weight: 700;
}

.setter-choice {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}
</style>